import React, { useMemo } from "react";
import classNames from "classnames";
import { get } from "lodash";
import { FieldArray } from "react-final-form-arrays";
import { useSelector } from "react-redux";
import { Tooltip } from "@blueprintjs/core";
import HiddenInput from "rs-components/Input/components/HiddenInput";
import Input from "rs-components/Input";
import ColumnName from "../ColumnName";
import TableRow from "../TableRow";
import ViewSelect from "../ViewSelect";
import { TableElementFormField } from "pages/RS-Workspace/components/Results/components/ElementDialog";
import { ResultSetLabels } from "shared/labels";
import { getCalculationTableResultSelector } from "store/rs-workspace/selectors/results.selectors";
import { ResultElementParameterType, } from "store/rs-workspace/rs-workspace.types";
import ErrorIcon from "icons/info_triangle.svg";
import * as styles from "./styles.module.scss";
const Table = ({ elementData, editMode = false, columnThreeDotsMenuRefs, viewSelectRefs, resultSetId, elementId, setShowCopiedToClipboardMessage, upsert, remove, move, }) => {
    const { resultRows } = useSelector(getCalculationTableResultSelector({ resultSetId, resultSetElementId: elementId }));
    const someResultBiased = useMemo(() => resultRows?.some((resultRow) => {
        const rowExist = elementData?.rows?.some((row) => row.id === resultRow.rowId);
        if (!rowExist) {
            return false;
        }
        return resultRow.columns.some((column) => {
            const columnExist = resultRow.columns.some(({ columnId }) => elementData?.columns?.some((column) => column.id === columnId));
            if (!columnExist) {
                return false;
            }
            return column.errorCount > 0;
        });
    }), [elementData?.columns, elementData?.rows, resultRows]);
    const tableHeader = useMemo(() => {
        const columns = get(elementData, TableElementFormField.columns);
        return (React.createElement("thead", null,
            React.createElement("tr", { className: classNames(columns.length === 0 ? styles["common-row"] : undefined) },
                React.createElement("th", { className: classNames(styles["row-item"], styles["row-item-firstColumn"]) },
                    React.createElement("div", { className: styles.errorWrapper },
                        React.createElement("p", { className: classNames({ [styles.error]: someResultBiased }) }, someResultBiased && (React.createElement(Tooltip, { minimal: true, content: "At least one calculated value is biased.", placement: "bottom", popoverClassName: styles.tooltip, rootBoundary: "viewport", openOnTargetFocus: false },
                            React.createElement(ErrorIcon, { className: styles["error-icon"] })))))),
                columns.map((column, index) => (React.createElement("th", { key: index, className: styles["row-item"] },
                    React.createElement(ColumnName, { editable: editMode, threeDotsPopoverRef: (el) => (columnThreeDotsMenuRefs.current[index] = el), column: column, columns: get(elementData, TableElementFormField.columns), rows: get(elementData, TableElementFormField.rows), deleteColumn: (id) => remove(id, TableElementFormField.columns), moveColumns: (positions) => move(positions, TableElementFormField.columns), upsert: (values) => upsert(values, TableElementFormField.columns) })))))));
    }, [columnThreeDotsMenuRefs, editMode, elementData, move, remove, upsert, someResultBiased]);
    const tableBody = useMemo(() => {
        const columns = get(elementData, TableElementFormField.columns);
        const rows = get(elementData, TableElementFormField.rows);
        if (rows.length === 0 && columns.length === 0) {
            return (React.createElement("tbody", null,
                React.createElement("tr", { className: styles["common-row"] },
                    React.createElement("td", { className: classNames(styles["row-item"], styles["row-item-firstColumn"]) },
                        React.createElement("p", { className: styles.emptyMessage }, ResultSetLabels.EMPTY_TABLE)))));
        }
        const tableRowProps = {
            rows,
            resultSetId,
            elementId,
            columns,
            setShowCopiedToClipboardMessage,
            deleteRow: (id) => remove(id, TableElementFormField.rows),
            moveRows: (positions) => move(positions, TableElementFormField.rows),
            upsert: (values) => upsert(values, TableElementFormField.rows),
        };
        return (React.createElement("tbody", null,
            columns.length > 0 && rows.length > 0 && (React.createElement("tr", { className: styles.viewRow },
                React.createElement("td", { className: classNames(styles["row-item"], styles["row-item-firstColumn"]) },
                    React.createElement("p", { className: styles.viewName }, "View")),
                columns.map((column, index) => (React.createElement("td", { key: index, className: styles["row-item"] }, column.parameter.type === ResultElementParameterType.INSIGHT ||
                    column.parameter.type === ResultElementParameterType.PROCESS_VARIABLE ? (React.createElement(ViewSelect, { editMode: editMode, column: column, resultSetId: resultSetId, elementId: elementId, selectRef: (el) => (viewSelectRefs.current[index] = el), editColumn: (values) => upsert(values, TableElementFormField.columns) })) : undefined))))),
            editMode ? (React.createElement(FieldArray, { name: `${TableElementFormField.rows}` }, ({ fields }) => fields.map((name) => (React.createElement(TableRow, { key: name, index: name, row: get(elementData, name), editMode: editMode, ...tableRowProps }))))) : (rows.map((row, index) => React.createElement(TableRow, { key: index, index: index, row: row, ...tableRowProps })))));
    }, [
        editMode,
        elementData,
        elementId,
        move,
        remove,
        resultSetId,
        setShowCopiedToClipboardMessage,
        upsert,
        viewSelectRefs,
    ]);
    return (React.createElement(React.Fragment, null,
        editMode && React.createElement(Input, { component: HiddenInput, name: `${TableElementFormField.columns}` }),
        editMode && get(elementData, TableElementFormField.rows).length === 0 && (React.createElement(Input, { component: HiddenInput, name: `${TableElementFormField.rows}` })),
        React.createElement("table", { className: styles.table },
            tableHeader,
            tableBody)));
};
export default Table;
